/**
 * ChatRoomTabs.tsx
 *
 * Room selection tabs for switching between different marketplace channels.
 * Extracted from ChatWrapper.v2.tsx to reduce duplication.
 */

import { FC } from "react"
import { useDispatch } from "react-redux"
import { toAbsoluteUrl, getIconChannelUrl } from "src/resources/helpers"
import { Collaboration, Room } from "src/app/models/Chat"
import * as chatV2 from "src/app/modules/chat/redux/ChatSlice.v2"

type ChatType = "allChat" | "myChat"

type ChatRoomTabsProps = {
  collaboration: Collaboration
  selectedRoom?: Room
  activeTab: ChatType
  onHiddenButtonsChange?: (buttons: HiddenButton[]) => void
  isMobile?: boolean
}

type HiddenButton = {
  id: string
  buttonText: string
}

const MARKETPLACE_NAMES = [
  "lazada",
  "tiktokshop",
  "tiktok",
  "shopee",
  "tokopedia",
]

const cleanMarketplaceName = (input: string | undefined | null): string => {
  if (!input) return ""
  const pattern = new RegExp(MARKETPLACE_NAMES.join("|"), "gi")
  return input.replace(pattern, "").trim()
}

const ChatRoomTabs: FC<ChatRoomTabsProps> = ({
  collaboration,
  selectedRoom,
  activeTab,
  isMobile = false,
}) => {
  const dispatch = useDispatch()

  if (!collaboration?.roomsModel?.length) {
    return null
  }

  const handleRoomSelect = (roomId: string) => {
    dispatch(
      chatV2.setSelectedCollaboration({
        chatType: activeTab,
        id: collaboration.id,
      })
    )
    dispatch(
      chatV2.setSelectedRoom({
        chatType: activeTab,
        id: roomId,
      })
    )
  }

  return (
    <div
      id="container-button"
      className={`d-flex flex-row p-0 m-0 ${isMobile ? "" : "pt-1"} w-100 hide-scrollbar`}
      style={{
        overflowX: "auto",
        gap: isMobile ? "4px" : "4px",
        WebkitOverflowScrolling: "touch", // Smooth scrolling on iOS
      }}
    >
      {collaboration.roomsModel.map((item: any) => {
        const bubble = item?.unreadCount || 0
        if (item?.accountsModel !== null && item.accountData) {
          return (
            <button
              id={item.id}
              className="d-flex align-items-center justify-content-start m-0 position-relative"
              style={{
                backgroundColor:
                  item.id === selectedRoom?.id ? "#dff5ff" : "#e4e6ef",
                border:
                  item.id === selectedRoom?.id
                    ? "1px solid #009ef7"
                    : "1px solid #a1a5b7",
                borderRadius: "6px",
                color: item.id === selectedRoom?.id ? "#009ef7" : "#a1a5b7",
                fontWeight: 500,
                fontSize: isMobile ? "12px" : "11px",
                height: "28px",
                padding: "4px 8px",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
              key={item.id}
              onClick={(e) => {
                e.preventDefault()
                handleRoomSelect(item.id)
              }}
            >
              {bubble > 0 && !isMobile && (
                <span
                  className="badge text-white p-0 m-0 position-absolute"
                  style={{
                    top: "-4px",
                    right: "-4px",
                    minWidth: "16px",
                    minHeight: "16px",
                    lineHeight: "16px",
                    borderRadius: "50%",
                    textAlign: "center",
                    backgroundColor: "#ff0000",
                    color: "#ffffff",
                    fontSize: "10px",
                    zIndex: 10,
                  }}
                >
                  {bubble}
                </span>
              )}
              <img
                src={toAbsoluteUrl(getIconChannelUrl(item?.accountData?.type))}
                alt={item?.accountData?.type}
                height={16}
                width={16}
                style={{ objectFit: "cover", borderRadius: "50%" }}
              />
              <span>
                {cleanMarketplaceName(
                  item?.accountData?.name
                    ? item?.accountData?.name
                    : item?.accountData?.businessName
                    ? item?.accountData?.businessName
                    : item?.accountData?.type
                )}
              </span>
            </button>
          )
        }
        return null
      })}
    </div>
  )
}

export default ChatRoomTabs
